// src/layouts/crm/service-types/index.jsx
/* eslint-disable react/prop-types */
// Service Types Management page with drawer for create/edit
// Uses Supabase table: public.service_types
// Imports follow Material Dashboard 2 React conventions used in the project.

import { useEffect, useMemo, useState, useCallback } from "react";

// React Router (optional: if you need navigate)
// import { useNavigate } from "react-router-dom";

// MUI
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Drawer from "@mui/material/Drawer"; // added
import Divider from "@mui/material/Divider"; // added
import Button from "@mui/material/Button"; // added

// Material Dashboard components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import DataTable from "examples/Tables/DataTable"; // added

// Supabase
import { supabase } from "lib/supa";
import PropTypes from "prop-types"; // added
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material"; // added

// ==========================
// Types & Helpers
// ==========================
const UNITS = [
  { value: "days", label: "ημέρες" },
  { value: "months", label: "μήνες" },
  { value: "years", label: "έτη" },
];

function formatDuration(v, u) {
  if (!v || !u) return "—";
  const unit = UNITS.find((x) => x.value === u)?.label || u;
  return `${v} ${unit}`;
}

// ==========================
// Drawer Component (Create/Edit)
// ==========================
function ServiceTypeDrawer({ open, onClose, initial, onSaved }) {
  // initial: existing row or null for create
  const isEdit = Boolean(initial?.id);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    description: "",
    default_duration_value: "",
    default_duration_unit: "months",
    is_active: true,
  });
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setError("");
      setSaving(false);
      setForm({
        name: initial?.name ?? "",
        description: initial?.description ?? "",
        default_duration_value:
          initial?.default_duration_value != null ? String(initial.default_duration_value) : "",
        default_duration_unit: initial?.default_duration_unit ?? "months",
        is_active: initial?.is_active ?? true,
      });
    }
  }, [open, initial]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const handleToggleActive = (e) => {
    setForm((s) => ({ ...s, is_active: e.target.checked }));
  };

  const handleSave = async () => {
    setError("");
    // Basic validation
    if (!form.name.trim()) {
      setError("Το όνομα είναι υποχρεωτικό");
      return;
    }
    if (form.default_duration_value && !form.default_duration_unit) {
      setError("Επιλέξτε μονάδα διάρκειας");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        name: form.name.trim(),
        description: form.description?.trim() || null,
        default_duration_value:
          form.default_duration_value === "" ? null : Number(form.default_duration_value),
        default_duration_unit:
          form.default_duration_value === "" ? null : form.default_duration_unit,
        is_active: !!form.is_active,
      };

      let resp;
      if (isEdit) {
        resp = await supabase
          .from("service_types")
          .update(payload)
          .eq("id", initial.id)
          .select()
          .single();
      } else {
        resp = await supabase.from("service_types").insert(payload).select().single();
      }

      if (resp.error) throw resp.error;

      onSaved?.(resp.data);
      onClose?.();
    } catch (err) {
      console.error("save service_type", err);
      if (String(err?.message || "").includes("uq_service_types_account_name")) {
        setError("Υπάρχει ήδη τύπος υπηρεσίας με αυτό το όνομα");
      } else {
        setError(err?.message || "Σφάλμα αποθήκευσης");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 560, // match templates wider drawer
          p: 2,
          bgcolor: "background.default",
          color: "text.primary",
        },
      }}
    >
      <MDBox p={2}>
        <MDTypography variant="h6" gutterBottom>
          {isEdit ? "Επεξεργασία τύπου υπηρεσίας" : "Νέος τύπος υπηρεσίας"}
        </MDTypography>
      </MDBox>
      <Divider />
      <MDBox p={2} display="flex" flexDirection="column" gap={2}>
        <TextField label="Όνομα*" name="name" value={form.name} onChange={handleChange} fullWidth />
        <TextField
          label="Περιγραφή"
          name="description"
          value={form.description}
          onChange={handleChange}
          multiline
          minRows={2}
          fullWidth
        />
        <TextField
          label="Διάρκεια (αριθμός)"
          name="default_duration_value"
          value={form.default_duration_value}
          onChange={handleChange}
          type="number"
          fullWidth
          inputProps={{ min: 0 }}
        />
        <TextField
          select
          label="Μονάδα διάρκειας"
          name="default_duration_unit"
          value={form.default_duration_unit}
          onChange={handleChange}
          fullWidth
          disabled={!form.default_duration_value && form.default_duration_value !== 0}
        >
          {UNITS.map((u) => (
            <MenuItem key={u.value} value={u.value}>
              {u.label}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          control={<Switch checked={!!form.is_active} onChange={handleToggleActive} />}
          label="Ενεργό"
        />

        {error ? (
          <MDTypography variant="button" color="error">
            {error}
          </MDTypography>
        ) : null}

        <MDBox mt={1} display="flex" gap={1}>
          <Button
            variant="contained"
            color="success"
            onClick={handleSave}
            disabled={saving}
            sx={{
              bgcolor: "success.main",
              color: "success.contrastText",
              "&:hover": { bgcolor: "success.dark" },
            }}
          >
            {saving ? "Αποθήκευση…" : "Αποθήκευση"}
          </Button>
          <Button variant="outlined" onClick={onClose} disabled={saving}>
            Άκυρο
          </Button>
        </MDBox>
      </MDBox>
    </Drawer>
  );
}

ServiceTypeDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  initial: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    default_duration_value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    default_duration_unit: PropTypes.oneOf(["days", "months", "years"]),
    is_active: PropTypes.bool,
  }),
  onSaved: PropTypes.func,
};

ServiceTypeDrawer.defaultProps = {
  onClose: undefined,
  onSaved: undefined,
  initial: null,
};

// ==========================
// Main Page
// ==========================
export default function ServiceTypesPage() {
  // const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState(null); // row object
  const [confirm, setConfirm] = useState({ open: false, id: null, name: "" });
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data, error: err } = await supabase
        .from("service_types")
        .select(
          "id, name, description, default_duration_value, default_duration_unit, is_active, created_at, updated_at"
        )
        .order("name", { ascending: true });
      if (err) throw err;
      setRows(data || []);
    } catch (e) {
      console.error("load service_types", e);
      setError(e?.message || "Σφάλμα φόρτωσης");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // Columns (like templates)
  const columns = useMemo(
    () => [
      {
        Header: "Ενεργειες",
        accessor: "actions",
        width: "120px",
        disableSortBy: true,
        Cell: ({ row }) => (
          <MDBox display="flex" alignItems="center" gap={0.5}>
            <Tooltip title="Επεξεργασία">
              <IconButton size="small" color="warning" onClick={() => handleEdit(row.original)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Διαγραφή">
              <IconButton
                size="small"
                color="error"
                onClick={() =>
                  setConfirm({ open: true, id: row.original.id, name: row.original.name })
                }
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </MDBox>
        ),
      },
      { Header: "Ονομα", accessor: "name", disableSortBy: true },
      { Header: "Περιγραφη", accessor: "description", disableSortBy: true },
      { Header: "Προεπιλεγμενη διαρκεια", accessor: "duration", disableSortBy: true },
      {
        Header: "Ενεργo",
        accessor: "is_active",
        disableSortBy: true,
        Cell: ({ value }) => (
          <Chip label={value ? "Ναι" : "Όχι"} color={value ? "success" : "error"} size="small" />
        ),
      },
    ],
    []
  );

  // Apply search to rows before mapping to DataTable
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.name, r.description, formatDuration(r.default_duration_value, r.default_duration_unit)]
        .filter(Boolean)
        .some((x) => String(x).toLowerCase().includes(q))
    );
  }, [rows, search]);

  const data = useMemo(
    () => ({
      columns,
      rows: filtered.map((r) => ({
        ...r,
        description: r.description || "—",
        duration: formatDuration(r.default_duration_value, r.default_duration_unit),
        actions: "", // filled by Cell renderer
      })),
    }),
    [filtered, columns]
  );

  const handleCreate = () => {
    setSelected(null);
    setDrawerOpen(true);
  };

  const handleEdit = (row) => {
    setSelected(row);
    setDrawerOpen(true);
  };

  const handleSaved = () => {
    fetchRows();
  };

  const handleDelete = async () => {
    const id = confirm.id;
    if (!id) return;
    try {
      const { error: err } = await supabase.from("service_types").delete().eq("id", id);
      if (err) throw err;
      setConfirm({ open: false, id: null, name: "" });
      fetchRows();
    } catch (e) {
      console.error("delete service_type", e);
      setConfirm({ open: false, id: null, name: "" });
      alert(e?.message || "Δεν ήταν δυνατή η διαγραφή");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              {/* Header */}
              <MDBox
                mx={2}
                mt={-3}
                py={1}
                px={2}
                variant="gradient"
                bgColor="success"
                borderRadius="lg"
                coloredShadow="success"
              >
                <MDBox display="flex" alignItems="center" justifyContent="space-between">
                  <MDTypography variant="h6" color="white">
                    ΤΥΠΟΙ ΥΠΗΡΕΣΙΩΝ
                  </MDTypography>
                  <Tooltip title="ΝΕΟΣ ΤΥΠΟΣ" arrow>
                    <MDAvatar
                      size="sm"
                      bgColor="dark"
                      sx={{ cursor: "pointer" }}
                      onClick={handleCreate}
                    >
                      <Icon sx={{ color: "white" }}>add</Icon>
                    </MDAvatar>
                  </Tooltip>
                </MDBox>
              </MDBox>

              {/* Search bar (left aligned, like templates) */}
              <MDBox px={2} pt={2} pb={1} display="flex" justifyContent="flex-start">
                <TextField
                  placeholder="Αναζήτηση…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  size="small"
                  sx={{ minWidth: 260 }}
                />
              </MDBox>

              {/* Body -> DataTable (like templates) */}
              {loading ? (
                <MDBox p={3} display="flex" justifyContent="center">
                  <CircularProgress />
                </MDBox>
              ) : (
                <DataTable
                  table={data}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  canSearch={false}
                  noEndBorder
                />
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Create/Edit Drawer */}
      <ServiceTypeDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        initial={selected}
        onSaved={handleSaved}
      />

      {/* Delete confirm */}
      <Dialog
        open={confirm.open}
        onClose={() => setConfirm({ open: false, id: null, name: "" })}
        PaperProps={{
          sx: { bgcolor: "background.default", color: "text.primary", borderRadius: 2 },
        }}
      >
        <DialogTitle sx={{ bgcolor: "dark.main", color: "dark.contrastText" }}>
          Διαγραφή τύπου υπηρεσίας
        </DialogTitle>
        <DialogContent sx={{ bgcolor: "background.default", color: "text.primary" }}>
          <MDTypography>
            Θα διαγραφεί ο τύπος <strong>{confirm.name}</strong>. Είστε σίγουροι;
          </MDTypography>
        </DialogContent>
        <DialogActions sx={{ bgcolor: "background.default" }}>
          <Button onClick={() => setConfirm({ open: false, id: null, name: "" })}>Άκυρο</Button>
          <Button color="error" onClick={handleDelete} startIcon={<Icon>delete</Icon>}>
            Διαγραφή
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
